import React from "react";
import { CCButton } from "@pymblecitizencoders/cc-ui";

/**
 * What the two sentinels at the top and bottom of a browse view say.
 *
 * Shared by the List and the Cards grid, which had the same four strings each
 * and would otherwise drift apart the first time one of them was corrected.
 *
 * ## Why the browse copy was wrong, and what replaced it
 *
 * The edges used to read "Scroll up for earlier months." and "Scroll down for
 * later months." in every state. That is only true while the panel is long
 * enough to scroll: `useDayWindow` guards both sentinels on `scrollable()`,
 * because a view narrowed to a handful of events has both of them on screen
 * from first paint and `maxPages` evicts a month for every month gained, so
 * without the guard it would page through the calendar for ever untouched.
 *
 * The guard is right. The copy was not: a filtered view that fits in the panel
 * showed two lines telling the reader to scroll, in a box with nothing to
 * scroll, while events matching their filter sat unfetched in the next month.
 * At the ends, the edge is now a BUTTON that does the thing the sentence used
 * to only describe.
 */

/** The state of a search, for the edges of a result list. */
export interface SearchEdges {
	/** Matches on the server, before the rail's own facets. Null if unknown. */
	total: number | null;
	/** How many have been fetched so far. */
	loaded: number;
	loadingMore: boolean;
	hasMore: boolean;
	onLoadMore: () => void;
}

/** The state of a month-paged browse, for the edges of a calendar range. */
export interface BrowseEdges {
	loadingEarlier?: boolean;
	loadingLater?: boolean;
	/** Ask for the month before the earliest in hand. */
	onLoadEarlier?: () => void;
	/** Ask for the month after the latest in hand. */
	onLoadLater?: () => void;
	/**
	 * False while the panel is too short to scroll, which is exactly when the
	 * sentinels stop firing and the reader needs a button instead.
	 */
	scrollable: boolean;
}

const count = (n: number): string => n.toLocaleString("en-AU");

/** "1 matching event" / "1,204 matching events". */
const matches = (n: number): string =>
	`${count(n)} matching event${n === 1 ? "" : "s"}`;

interface EdgeProps {
	/** True when the window reaches that end of the days in hand. */
	atEdge: boolean;
	search?: SearchEdges;
	browse: BrowseEdges;
}

export const TopEdge: React.FC<EdgeProps> = ({ atEdge, search, browse }) => {
	if (search) {
		/* A search result set has no "earlier months" to fetch — every match is
		   already counted, and the ones not yet fetched are at the END of it. */
		return <>{atEdge ? "Start of results." : "Scroll up for earlier days."}</>;
	}
	if (browse.loadingEarlier) return <>Loading earlier events...</>;
	if (!atEdge) return <>Scroll up for earlier days.</>;
	if (!browse.scrollable && browse.onLoadEarlier) {
		return (
			<CCButton variant="outline" size="sm" onClick={browse.onLoadEarlier}>
				Load earlier months
			</CCButton>
		);
	}
	return <>Scroll up for earlier months.</>;
};

export const BottomEdge: React.FC<EdgeProps> = ({ atEdge, search, browse }) => {
	if (search) {
		if (!atEdge) return <>Scroll down for later days.</>;
		if (search.loadingMore) return <>Loading more matches...</>;
		return (
			<>
				{/* The number is the SERVER's, before the rail's other facets, so it
				    is stated as what the search matched rather than as what is on
				    screen — the two differ the moment an event type is ticked. */}
				<span className="em-browse-edge__note">
					{search.total === null
						? `${count(search.loaded)} loaded.`
						: search.hasMore
							? `Showing ${count(search.loaded)} of ${matches(search.total)}.`
							: `${matches(search.total)}.`}
				</span>
				{search.hasMore && (
					<CCButton variant="outline" size="sm" onClick={search.onLoadMore}>
						Load more
					</CCButton>
				)}
			</>
		);
	}
	if (browse.loadingLater) return <>Loading later events...</>;
	if (!atEdge) return <>Scroll down for later days.</>;
	if (!browse.scrollable && browse.onLoadLater) {
		return (
			<CCButton variant="outline" size="sm" onClick={browse.onLoadLater}>
				Load later months
			</CCButton>
		);
	}
	return <>Scroll down for later months.</>;
};
